/**
 * NativeAzureSpeech Web Implementation
 *
 * Browser-based pronunciation assessment using Azure Speech SDK JavaScript.
 * This file is automatically loaded for web platform by React Native.
 *
 * Platform Resolution:
 * - iOS/Android: NativeAzureSpeech.native.ts (TurboModule)
 * - Web: NativeAzureSpeech.web.ts (this file)
 */

import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import type {
  AssessmentConfig,
  PronunciationResult,
  SpeechRecognitionConfig,
  SpeechTranscriptionResult,
  RecognitionEvent,
  Spec,
} from './NativeAzureSpeech';
import { createLogger } from '../utils/logger';
import { SpeechLogger } from './AzureSpeechLogger';
import { webEventEmitter } from './webEventEmitter';

// Logger instance — recreated per session with config flags
const slog = new SpeechLogger(createLogger('AzureSpeech.Web'), false, false);

/**
 * Create an Error with a `code` property, matching the native bridge's
 * `reject(code, message)` behavior on iOS/Android.
 */
function codedError(code: string, message: string): Error {
  const err = new Error(message);
  (err as Error & { code: string }).code = code;
  return err;
}

function cancellationErrorName(errorCode: SpeechSDK.CancellationErrorCode): string {
  switch (errorCode) {
    case SpeechSDK.CancellationErrorCode.ConnectionFailure: return 'connection_failure';
    case SpeechSDK.CancellationErrorCode.ServiceTimeout: return 'service_timeout';
    case SpeechSDK.CancellationErrorCode.AuthenticationFailure: return 'auth_failure';
    case SpeechSDK.CancellationErrorCode.Forbidden: return 'forbidden';
    case SpeechSDK.CancellationErrorCode.TooManyRequests: return 'rate_limited';
    case SpeechSDK.CancellationErrorCode.BadRequestParameters: return 'bad_request';
    case SpeechSDK.CancellationErrorCode.ServiceError: return 'service_error';
    case SpeechSDK.CancellationErrorCode.RuntimeError: return 'runtime_error';
    case SpeechSDK.CancellationErrorCode.NoError: return 'canceled';
    default: return 'unknown_error';
  }
}

// Current recognizer instance
let recognizer: SpeechSDK.SpeechRecognizer | null = null;
let isCurrentlyRecognizing = false;

// Continuous recognition synchronization (no locks needed — JS is single-threaded)
let resultCaptured = false;
let stopRequested = false;
let stopRequestedAt = 0;

// Post-stop safety: stored callbacks so stopRecognition can settle the promise
let pendingReject: ((err: Error) => void) | null = null;
// Called after stopContinuousRecognitionAsync completes — runs the merge+resolve logic
let pendingComplete: (() => void) | null = null;
let stopSafetyTimer: ReturnType<typeof setTimeout> | null = null;
const stopSafetyTimeoutMs = 5000;
let endOfTurnTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Emit event to all registered listeners
 */
function emitEvent(eventName: string, event: RecognitionEvent) {
  slog.sdkEvent('EmitEvent', null, { eventName, type: event.type });
  webEventEmitter.emit(eventName, event);
}

/**
 * Clean up recognizer and reset state
 */
function cleanup() {
  if (endOfTurnTimer) {
    clearTimeout(endOfTurnTimer);
    endOfTurnTimer = null;
  }
  if (stopSafetyTimer) {
    clearTimeout(stopSafetyTimer);
    stopSafetyTimer = null;
  }
  pendingReject = null;
  pendingComplete = null;
  if (recognizer) {
    try {
      recognizer.close();
    } catch (e) {
      slog.sdkAnomaly('CloseRecognizerError', e);
    }
    recognizer = null;
  }
  isCurrentlyRecognizing = false;
}

/**
 * Web implementation of Azure Speech native module
 */
const NativeAzureSpeechWeb: Spec = {
  /**
   * Start pronunciation assessment — NOT SUPPORTED ON WEB.
   *
   * The STT / pronunciation-assessment recognizer routes through the backend
   * WebSocket relay (server-speech-proxy Phase 2), which was device-verified on
   * native only. This entry rejects with `not_supported`; the UI gates it via
   * SPEECH_RECOGNITION_AVAILABLE so it is not reached in normal use.
   */
  async startPronunciationAssessment(
    _host: string,
    _token: string,
    _config: AssessmentConfig,
  ): Promise<PronunciationResult> {
    // Web is intentionally degraded for the STT / pronunciation-assessment relay
    // (server-speech-proxy Phase 2). The relay auth — the SDK forwarding the
    // Supabase JWT as an Authorization: Bearer header on the WS upgrade — was
    // device-verified on native only; the browser Speech SDK's upgrade path is
    // not wired to the relay yet. The UI hides these affordances on web via
    // SPEECH_RECOGNITION_AVAILABLE; this reject is defense-in-depth for any code
    // path that still reaches the module.
    return Promise.reject(
      codedError('not_supported', 'Speech recognition is not available on web'),
    );
  },

  /**
   * Start free-form speech recognition (single-shot, no pronunciation scoring)
   * Used for conversation mode.
   */
  async startSpeechRecognition(
    token: string,
    region: string,
    config: SpeechRecognitionConfig
  ): Promise<SpeechTranscriptionResult> {
    return new Promise((resolve, reject) => {
      try {
        if (isCurrentlyRecognizing) {
          reject(codedError('already_recognizing', 'Recognition already in progress'));
          return;
        }

        if (recognizer) {
          recognizer.close();
          recognizer = null;
        }

        isCurrentlyRecognizing = true;

        const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
        speechConfig.speechRecognitionLanguage = config.language;

        if (config.silenceTimeoutMs != null) {
          speechConfig.setProperty(
            SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs,
            String(config.silenceTimeoutMs),
          );
        }

        const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
        recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

        // ── Phrase list (boost recognition of specific words) ──
        if (config.phraseList?.length) {
          const phraseListGrammar = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
          for (const phrase of config.phraseList) {
            phraseListGrammar.addPhrase(phrase);
          }
        }

        slog.sdkEvent('StartFreeFormRecognition', 'start', { lang: config.language });

        recognizer.recognizing = (_s, e) => {
          if (e.result.text) {
            emitEvent('onRecognizing', {
              type: 'recognizing',
              text: e.result.text,
            });
          }
        };

        recognizer.recognizeOnceAsync(
          (result) => {
            cleanup();

            if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
              slog.sdkEvent('FreeFormRecognition', 'end', { text: result.text });
              resolve({
                transcription: result.text || '',
                durationMs: result.duration / 10000,
              });
            } else if (result.reason === SpeechSDK.ResultReason.NoMatch) {
              slog.sdkEvent('FreeFormNoMatch');
              reject(codedError('no_match', 'No speech detected. Please try again.'));
            } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
              const cancellation = SpeechSDK.CancellationDetails.fromResult(result);
              const errorName = cancellationErrorName(cancellation.ErrorCode);
              slog.sdkAnomaly('FreeFormCanceled', { code: errorName, error: cancellation.errorDetails });
              reject(codedError('canceled', errorName));
            } else {
              reject(codedError('error', 'Recognition failed with unknown reason'));
            }
          },
          (error) => {
            slog.sdkAnomaly('FreeFormRecognizeError', { error });
            cleanup();
            reject(codedError('exception', `Recognition failed: ${error}`));
          }
        );

      } catch (error: unknown) {
        cleanup();
        const errorMessage = error instanceof Error ? error.message : 'Failed to start speech recognition';
        reject(codedError('exception', errorMessage));
      }
    });
  },

  /**
   * Stop ongoing recognition
   */
  stopRecognition(): void {
    try {
      if (!isCurrentlyRecognizing) {
        slog.sdkAnomaly('A12:StopNotRecognizing');
        return;
      }

      if (resultCaptured) {
        slog.sdkAnomaly('A12:StopAlreadyCaptured');
        return;
      }

      // Cancel end-of-turn timer (user pressed stop manually)
      if (endOfTurnTimer) {
        clearTimeout(endOfTurnTimer);
        endOfTurnTimer = null;
      }

      slog.sdkEvent('StopRecognition', 'start', { resultCaptured, stopRequested });

      if (stopRequested) {
        // A12: double-stop blocked by guard — SDK never sees it
        slog.sdkAnomaly('A12:DoubleStopBlocked', { msSinceFirstStop: Date.now() - stopRequestedAt });
        return;
      }

      if (recognizer) {
        stopRequested = true;
        stopRequestedAt = Date.now();
        recognizer.stopContinuousRecognitionAsync(
          () => {
            // stopContinuousRecognitionAsync completed. In unscripted pronunciation
            // assessment mode, the SDK consistently does NOT fire canceled(EndOfStream)
            // after stop. Call pendingComplete to merge segments and resolve the promise.
            if (!resultCaptured && pendingComplete) {
              resultCaptured = true;
              slog.sdkEvent('StopRecognition', 'end');
              if (stopSafetyTimer) {
                clearTimeout(stopSafetyTimer);
                stopSafetyTimer = null;
              }
              const completeFn = pendingComplete;
              pendingComplete = null;
              completeFn();
            }
          },
          (error) => {
            slog.sdkAnomaly('StopRecognitionFailed', error);
            cleanup();
          }
        );

        // Safety fallback: if stopContinuousRecognitionAsync itself hangs
        // (success/error callbacks never fire), force-reject after 5s.
        stopSafetyTimer = setTimeout(() => {
          stopSafetyTimer = null;
          if (!resultCaptured && pendingReject) {
            resultCaptured = true;
            const rejectFn = pendingReject;
            pendingReject = null;
            const msg = `Post-stop safety — stopContinuousRecognitionAsync hung for ${stopSafetyTimeoutMs}ms, force-completing`;
            slog.sdkAnomaly('A16:StopTimeout', { msg });
            cleanup();
            rejectFn(codedError('stop_timeout', msg));
          }
        }, stopSafetyTimeoutMs);
      }
    } catch (error) {
      slog.sdkAnomaly('StopRecognitionException', error);
      cleanup();
    }
  },

  /**
   * Force-cancel ongoing recognition and discard the take. Delegates to the
   * regular stop path; the JS caller discards the resolved result.
   */
  cancelRecognition(): void {
    try {
      if (!isCurrentlyRecognizing) {
        slog.sdkAnomaly('A12:StopNotRecognizing');
        return;
      }
      NativeAzureSpeechWeb.stopRecognition();
    } catch (error) {
      slog.sdkAnomaly('CancelRecognitionException', error);
      cleanup();
    }
  },

  /**
   * Check if recognition is in progress
   */
  isRecognizing(): boolean {
    return isCurrentlyRecognizing;
  },

  /**
   * Add event listener (no-op on web — subscriptions go through webEventEmitter)
   */
  addListener(_eventName: string): void {
    // Handled by webEventEmitter
  },

  /**
   * Remove event listeners (no-op on web — subscriptions go through webEventEmitter)
   */
  removeListeners(_count: number): void {
    // Handled by webEventEmitter
  },
};

export default NativeAzureSpeechWeb;
